import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { GripHorizontal, ChevronDown, ChevronUp, X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { macSpring, macWindowVariants } from '../../utils/macAnimations';

interface DraggableFloatingWindowProps {
  title: string;
  icon?: React.ElementType;
  badge?: string;
  badgeColor?: 'emerald' | 'purple' | 'blue' | 'zinc' | 'pink';
  initialX?: number;
  initialY?: number;
  widthClass?: string;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export const DraggableFloatingWindow: React.FC<DraggableFloatingWindowProps> = ({
  title,
  icon: Icon,
  badge,
  badgeColor = 'blue',
  initialX,
  initialY,
  widthClass = 'w-88 sm:w-96',
  isOpen,
  onClose,
  children,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  
  // Calculate smart top positioning so window opens from top over the imposition layout
  const getInitialPosition = () => {
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const defaultX = initialX !== undefined 
      ? Math.max(16, Math.min(screenWidth - 380, initialX))
      : Math.max(16, screenWidth - 440);
    const defaultY = initialY !== undefined 
      ? Math.max(60, initialY)
      : 70;
    return { x: defaultX, y: defaultY };
  };

  const [position, setPosition] = useState(getInitialPosition);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [zIndex, setZIndex] = useState(99990);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // When reopened, ensure it starts cleanly from top visible bounds
  useEffect(() => {
    if (isOpen) {
      setPosition(prev => ({
        x: Math.max(16, Math.min(window.innerWidth - 380, prev.x)),
        y: Math.max(60, Math.min(window.innerHeight - 150, prev.y))
      }));
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const dragRef = useRef<{ mouseX: number; mouseY: number; posX: number; posY: number }>({
    mouseX: 0,
    mouseY: 0,
    posX: position.x,
    posY: position.y,
  });

  // Keep inside screen bounds on window resize
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => ({
        x: Math.min(prev.x, Math.max(10, window.innerWidth - 320)),
        y: Math.min(prev.y, Math.max(60, window.innerHeight - 100)),
      }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Prevent dragging if clicking close or collapse buttons
    if ((e.target as HTMLElement).closest('button')) return;

    setZIndex(99995); // Bring to front on interaction
    setIsDragging(true);
    dragRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      posX: position.x,
      posY: position.y,
    };
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    if (e.touches.length === 0) return;

    setZIndex(99995);
    const touch = e.touches[0];
    setIsDragging(true);
    dragRef.current = {
      mouseX: touch.clientX,
      mouseY: touch.clientY,
      posX: position.x,
      posY: position.y,
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const dx = e.clientX - dragRef.current.mouseX;
      const dy = e.clientY - dragRef.current.mouseY;

      const newX = Math.max(10, Math.min(window.innerWidth - 200, dragRef.current.posX + dx));
      const newY = Math.max(50, Math.min(window.innerHeight - 60, dragRef.current.posY + dy));

      setPosition({ x: newX, y: newY });
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging || e.touches.length === 0) return;
      const touch = e.touches[0];
      const dx = touch.clientX - dragRef.current.mouseX;
      const dy = touch.clientY - dragRef.current.mouseY;

      const newX = Math.max(10, Math.min(window.innerWidth - 200, dragRef.current.posX + dx));
      const newY = Math.max(50, Math.min(window.innerHeight - 60, dragRef.current.posY + dy));

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  if (!isOpen || !isMounted) return null;

  const badgeColorClasses = {
    emerald: 'bg-emerald-950/80 text-emerald-300 border-emerald-500/40',
    purple: 'bg-purple-950/80 text-purple-300 border-purple-500/40',
    blue: 'bg-blue-950/80 text-blue-300 border-blue-500/40',
    pink: 'bg-pink-950/80 text-pink-300 border-pink-500/40',
    zinc: 'bg-zinc-800 text-zinc-300 border-zinc-700',
  }[badgeColor];

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          variants={macWindowVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={macSpring}
          style={{
            position: 'fixed',
            left: `${position.x}px`,
            top: `${position.y}px`,
            zIndex,
          }}
          onPointerDown={() => setZIndex(99995)}
          className={`${widthClass} bg-zinc-900/95 backdrop-blur-md border border-zinc-700/90 rounded-xl shadow-2xl overflow-hidden select-none ${
            isDragging ? 'shadow-blue-900/40 ring-2 ring-blue-500/50' : 'ring-1 ring-zinc-700/60'
          }`}
        >
          {/* Draggable Title Header */}
          <div
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            className="px-3.5 py-2.5 bg-gradient-to-b from-zinc-800 to-zinc-900 border-b border-zinc-800 flex items-center justify-between select-none cursor-grab active:cursor-grabbing hover:bg-zinc-800/90 transition-colors"
            title="Click and drag anywhere to move panel over layout"
          >
            <div className="flex items-center gap-2 min-w-0 pointer-events-none">
              <GripHorizontal className="w-4 h-4 text-zinc-400 shrink-0 cursor-grab" />
              {Icon && <Icon className="w-4 h-4 text-blue-400 shrink-0" />}
              <span className="font-bold text-xs text-zinc-100 truncate">{title}</span>
              {badge && (
                <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider shrink-0 ${badgeColorClasses}`}>
                  {badge}
                </span>
              )}
            </div>

            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-1 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700/80 rounded transition-colors cursor-pointer"
                title={isCollapsed ? "Expand Panel" : "Collapse Panel"}
              >
                {isCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-1 text-zinc-400 hover:text-red-400 hover:bg-zinc-700/80 rounded transition-colors cursor-pointer"
                title="Close Panel (Esc)"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Collapsible Content Body with Mac-style smooth transition */}
          <AnimatePresence>
            {!isCollapsed && (
              <motion.div 
                initial={{ height: 0, opacity: 0, scaleY: 0.95, originY: 0 }}
                animate={{ height: 'auto', opacity: 1, scaleY: 1 }}
                exit={{ height: 0, opacity: 0, scaleY: 0.95 }}
                transition={macSpring}
                className="max-h-[78vh] overflow-y-auto custom-scrollbar bg-zinc-900/50 select-text"
              >
                <div 
                  className="p-3.5"
                  style={{
                    backgroundImage: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
                    backgroundSize: '16px 16px'
                  }}
                >
                  {children}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
